import {
  ChatAlt2Icon,
  MicrophoneIcon,
  PhoneIcon,
  VolumeUpIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import type { ComponentType, SVGProps } from 'react';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import Halftone from './Halftone';
import { CheckGlyph, delay, SectionHeading } from './primitives';
import { useReveal } from './useReveal';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

// A fixed, hand-shaped waveform: loud in the middle of a phrase.
const BARS = [
  4, 7, 12, 9, 16, 22, 14, 26, 34, 24, 40, 30, 46, 38, 52, 44, 58, 48, 40, 54,
  44, 36, 48, 30, 38, 26, 32, 20, 24, 16, 22, 12, 16, 9, 12, 6, 8, 4,
];

export default function AgentVoice() {
  const { t } = useLocale('chatbots');
  const { ref, state } = useReveal<HTMLDivElement>(0.2);

  const features: { icon: Icon; title: string; detail: string }[] = [
    {
      icon: PhoneIcon,
      title: t('Your own phone number'),
      detail: t(
        'Connect a Twilio number and every call goes straight to your agent.',
      ),
    },
    {
      icon: MicrophoneIcon,
      title: t('Voice in your chat widget'),
      detail: t('Visitors tap the call button and simply start talking.'),
    },
    {
      icon: VolumeUpIcon,
      title: t('A voice that fits your brand'),
      detail: t(
        'Pick from hundreds of natural voices for your widget, or use a clone of your own.',
      ),
    },
  ];

  const transcript = [
    { caller: true, text: t('Hi, do you have a table for four tonight?') },
    { caller: false, text: t('We do! Would 7:30 or 8:15 work for you?') },
    { caller: true, text: t('7:30, please.') },
    { caller: false, text: t('Booked for four at 7:30. See you tonight!') },
  ];

  return (
    <section
      id='voice'
      className='relative isolate overflow-hidden bg-[#0c0b1c] py-20 sm:py-28'
    >
      <Halftone
        at='0% 0%'
        className='opacity-30 [--ht:rgb(var(--tw-color-primary-800))]'
        reach={70}
      />
      <div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          invert
          title={t('Pick up every call.')}
          tail={t('Even the ones at 3 a.m.')}
          lead={t(
            'Connect a phone number and your agent answers calls in a natural voice. Visitors can also talk to it from your chat widget, and it handles interruptions like a person would.',
          )}
        />

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'mt-16')}
        >
          <p className='sr-only'>
            {t(
              'Example: an AI agent takes a phone call for a restaurant and books a table for four.',
            )}
          </p>
          <div
            aria-hidden='true'
            className='grid overflow-hidden rounded-[28px] bg-white/[0.035] ring-1 ring-inset ring-white/10 lg:grid-cols-[1.1fr_1fr] sm:rounded-[36px]'
          >
            <div className='relative flex min-w-0 flex-col justify-between gap-10 p-6 sm:p-10'>
              <div className='flex flex-wrap items-start justify-between gap-4'>
                <div>
                  <p className='flex items-center gap-2 text-sm font-medium text-emerald-400'>
                    <span className='h-2 w-2 animate-pulse rounded-full bg-emerald-400 motion-reduce:animate-none' />
                    {t('Connected')}
                    <span className='tabular-nums text-emerald-400/80'>
                      01:12
                    </span>
                  </p>
                  <p className='mt-3 text-2xl font-semibold text-white'>
                    Trattoria Rosa
                  </p>
                  <p className='mt-1 text-sm text-[#9d9dbd]'>
                    {t('Incoming call')} ·{' '}
                    <span className='tabular-nums' dir='ltr'>
                      +1 (415) 555-0178
                    </span>
                  </p>
                </div>
                <span className='rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80'>
                  {t('Answered by AI')}
                </span>
              </div>

              <div
                className={clsxm(
                  styles.wave,
                  styles.waveCenter,
                  'flex h-24 items-center justify-center gap-[3px] sm:h-28 sm:gap-[5px]',
                )}
              >
                {BARS.map((height, index) => (
                  <span
                    key={index}
                    className='w-[3px] flex-none rounded-full bg-primary-400 sm:w-[5px]'
                    style={{
                      height: `${Math.round(height * 1.7)}px`,
                      opacity: 0.35 + (height / 58) * 0.65,
                      ...delay(index * 55),
                    }}
                  />
                ))}
              </div>

              <div className='flex flex-wrap items-center gap-3'>
                <span className='flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500/15 px-3.5 py-2 text-sm font-medium text-primary-200'>
                  <span className='h-1.5 w-1.5 rounded-full bg-primary-300' />
                  {t('Agent is speaking')}
                </span>
                <span className='ms-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white'>
                  <MicrophoneIcon className='h-5 w-5' />
                </span>
                <span className='flex h-11 w-11 items-center justify-center rounded-full bg-[#e5484d] text-white'>
                  <PhoneIcon className='h-5 w-5 rotate-[135deg]' />
                </span>
              </div>
            </div>

            <div className='min-w-0 border-t border-white/10 bg-white/[0.03] p-6 sm:p-10 lg:border-s lg:border-t-0'>
              <p className='flex items-center gap-2 text-sm font-medium text-[#9d9dbd]'>
                <ChatAlt2Icon className='h-4 w-4' />
                {t('Live transcript')}
              </p>
              <ol className='mt-6 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-5'>
                {transcript.map(({ caller, text }, index) => (
                  <li
                    key={text}
                    className={clsxm(
                      styles.rv,
                      'col-span-2 grid grid-cols-subgrid',
                    )}
                    style={delay(200 + index * 450)}
                  >
                    <span
                      className={clsxm(
                        'mt-0.5 text-xs font-semibold uppercase tracking-wide',
                        caller ? 'text-[#7c7c9c]' : 'text-primary-300',
                      )}
                    >
                      {caller ? t('Caller') : t('Agent')}
                    </span>
                    <span
                      className={clsxm(
                        'text-[15px] leading-relaxed',
                        caller ? 'text-[#b9b9d4]' : 'text-white',
                      )}
                    >
                      {text}
                    </span>
                  </li>
                ))}
              </ol>
              <p
                className={clsxm(
                  styles.rv,
                  'mt-6 inline-flex items-center gap-2.5 rounded-full bg-emerald-400/10 py-1.5 pe-3.5 ps-1.5 text-sm font-medium text-emerald-300 ring-1 ring-inset ring-emerald-400/20',
                )}
                style={delay(2100)}
              >
                <span className='flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400 text-[#06281b]'>
                  <CheckGlyph className='h-3.5 w-3.5' />
                </span>
                {t('Table booked: tonight, 7:30, four guests')}
              </p>
            </div>
          </div>
        </div>

        <ul className='mt-14 grid gap-10 sm:grid-cols-3 sm:gap-8'>
          {features.map(({ icon: Icon, title, detail }) => (
            <li key={title}>
              <Icon className='h-6 w-6 text-primary-300' />
              <p className='mt-4 text-base font-semibold text-white'>{title}</p>
              <p className='mt-2 text-[15px] leading-7 text-[#a9a9c6]'>
                {detail}
              </p>
            </li>
          ))}
        </ul>
        <p className='mt-12 text-sm text-[#7f7fa0]'>
          {t(
            'Voice in the chat widget comes with Unlimited 200 and up. Try it free in the Playground.',
          )}
        </p>
      </div>
    </section>
  );
}
